import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Heading, IconGraduationCap, Paragraph, Text, Toast, token, useToast } from '@zillow/constellation';
import { useWizard, WizardStep } from '@zillow/zrs-rental-applications';
import {
  ALREADY_DECLINED_ERROR,
  BorderlessAlert,
  MAX_EMAIL_COUNT,
  Steps,
  useEmailTemplate,
  WITHDRAWN_ERROR,
} from '../DeclineApplicationPage.utils';
import { EmailEditor } from '../EmailEditor';
import { useSelector } from 'react-redux';
import { listingAliasSelector } from '../../../selectors';
import useDeclineApplication from '../../../utils/apis/useDeclineApplication';
import useQueryVariables from '../../../hooks/useQueryVariables';
import { RentingPledge } from '../RentingPledge';
import { LegalConsiderations } from '../LegalConsiderations';
import { addAction } from '../../../constants/datadogActions';
import { useListingUser } from '../../../hooks/useListingUser';
import {
  emitDeclineApplicationBasedOnScreeningEvent,
  emitDeclineApplicationEvent,
} from '../../../../client/analytics/clickstreamEvents';

const PreviewStep = ({ id, label, selectedDeclineReasons, isBasedOnScreening, allDeclineReasons }) => {
  const { user, listing } = useListingUser();
  const { enqueueToast } = useToast();
  const { onRequestStep } = useWizard();
  const [agreesToTerms, setAgreesToTerms] = useState(false);
  const { applicationId } = useQueryVariables();
  const listingAlias = useSelector(listingAliasSelector);
  const emailTemplate = useEmailTemplate(selectedDeclineReasons, allDeclineReasons);
  const [emailText, setEmailText] = useState(emailTemplate);

  const handleDeclineApplicationError = ({ data = {} }) => {
    addAction.applicationDeclinedFailure({ ...data, declineReasons: [...selectedDeclineReasons] });
    if (data?.responseType === WITHDRAWN_ERROR) {
      return onRequestStep(Steps.WithdrawnErrorStep);
    }
    if (data?.responseType === ALREADY_DECLINED_ERROR) {
      return enqueueToast(<Toast appearance="info" body="This application has already been declined." />);
    }

    enqueueToast(<Toast appearance="error" body="The message can't be delivered right now. Please try again." />);
  };

  const { mutateAsync: declineApplication } = useDeclineApplication({
    onError: handleDeclineApplicationError,
    onSuccess: ({ data = {} }) => {
      addAction.applicationDeclinedSuccess({ ...data, declineReasons: [...selectedDeclineReasons] });

      if (isBasedOnScreening) {
        emitDeclineApplicationBasedOnScreeningEvent(listing, user);
      } else {
        emitDeclineApplicationEvent(listing, user);
      }
    },
  });

  useEffect(() => {
    setEmailText(emailTemplate);
  }, [emailTemplate]);

  return (
    <WizardStep
      id={id}
      meta={{ isStepDisabled: !agreesToTerms || emailText.length > MAX_EMAIL_COUNT || emailText.length === 0 }}
      label={label}
      data-test="preview-step"
      onSubmit={() =>
        declineApplication({ listingAlias, applicationId, declineReasons: [...selectedDeclineReasons], emailText })
      }
      onSuccess={Steps.ConfirmationStep}
    >
      <Heading level={4} marginBottom="sm" data-test="preview-step__heading">
        Preview your email to the applicant
      </Heading>
      <Paragraph marginBottom="md" data-test="preview-step__message-info">
        {`This is the message the applicant will receive. We expect all landlords and property managers to treat every prospective tenant with fairness and respect. Edits to this message must adhere to this policy.`}
      </Paragraph>
      <RentingPledge value={agreesToTerms} onChange={({ target: { checked } }) => setAgreesToTerms(checked)} />
      {!isBasedOnScreening && (
        <BorderlessAlert
          icon={<IconGraduationCap fontColor="brand" size="lg" />}
          marginBottom={token('spacing.md')}
          data-test="preview-step__alert"
          body={
            <Text>
              {`Please ensure that you comply with federal, state, and local laws. For legal advice, consult a local attorney.`}
            </Text>
          }
        />
      )}
      <EmailEditor value={emailText} onChange={({ target: { value } }) => setEmailText(value)} />
      <Paragraph marginBottom={isBasedOnScreening ? 'md' : 'xl'}>
        {`To protect the privacy of the applicant, once this notice is sent, you will no longer be able to view application details and consumer reports associated with this application.`}
      </Paragraph>
      {isBasedOnScreening && <LegalConsiderations />}
    </WizardStep>
  );
};

PreviewStep.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.shape({
    email: PropTypes.string.isRequired,
    agreesToTerms: PropTypes.bool.isRequired,
  }),
  selectedDeclineReasons: PropTypes.instanceOf(Set).isRequired,
  isBasedOnScreening: PropTypes.bool.isRequired,
  allDeclineReasons: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      additionalRequirement: PropTypes.string.isRequired,
    }),
  ),
};

export default PreviewStep;
